import {
  faChevronsLeft,
  faChevronsRight,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useRouterState } from '@tanstack/react-router';
import { useEffect, useId, useRef, useState } from 'react';
import { IconButton } from '@/components/shared/IconButton';
import { RetracedMark } from '@/components/shared/RetracedMark';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useTranslator } from '@/hooks/useTranslator';
import { cn } from '@/lib/utils/cn';
import {
  activeChildKey,
  buildPanelSpecs,
  isPathActive,
  isSpecActive,
} from '@/lib/utils/sidebarNavHelpers';
import { useSidebarStore } from '@/store/sidebarStore';
import { ColumnGroup } from './ColumnGroup';
import { ColumnItem } from './ColumnItem';
import { ColumnSection } from './ColumnSection';
import { StripGroup } from './StripGroup';
import { StripItem } from './StripItem';
import {
  childBase,
  childInactive,
  childSelected,
  columnPad,
  columnStack,
  expandedColumnWidthCls,
  restingFg,
  rowBase,
  rowIcon,
  rowInactive,
} from './styles';
import type { NavEntry, PanelSpec } from './types';

// ---------- Root ----------

interface SidebarProps {
  /**
   * Entries to render. Which set they are — client app, management, whatever
   * comes next — is the shell's call, not this component's: see
   * `useSidebarNav`. Everything here (column, rail, flyout, active matching)
   * works the same whatever it is handed.
   */
  nav: NavEntry[];
}

/**
 * Two shapes of one nav tree.
 *
 * **Expanded** is the resting state: a single 232px column carrying both levels
 * at once — section headings, first-level rows with their labels, and each
 * unfolded group's pages nested directly beneath it. Nothing is hidden, so
 * nothing has to be hovered.
 *
 * **Collapsed** is the 56px icon rail, where the second level comes back as the
 * hover flyout. Choosing a page there re-expands the column with that section
 * unfolded, so collapsing is a detour rather than a mode you get stuck in.
 *
 * Touch has no hover, so mobile always renders the column.
 */
export default function Sidebar({ nav }: SidebarProps) {
  const pathname = useRouterState({ select: s => s.location.pathname });
  const { getTranslation } = useTranslator({ page: 'sidebar' });
  const panelSpecs = buildPanelSpecs(nav);

  const expanded = useSidebarStore(s => s.panelOpen);
  const openGroupKeys = useSidebarStore(s => s.openGroupKeys);
  const mobileOpen = useSidebarStore(s => s.mobileOpen);
  const setPanelOpen = useSidebarStore(s => s.setPanelOpen);
  const openGroup = useSidebarStore(s => s.openGroup);
  const openOnlyGroup = useSidebarStore(s => s.openOnlyGroup);
  const toggleGroupOpen = useSidebarStore(s => s.toggleGroupOpen);
  const setMobileOpen = useSidebarStore(s => s.setMobileOpen);
  const isMobile = useIsMobile();

  const sidebarId = useId();
  const [hoveredGroupKey, setHoveredGroupKey] = useState<string | null>(null);

  // Hover-intent: a short open delay (so the flyout doesn't pop on a quick
  // pass-over), and a close delay that bridges the gap as the cursor moves from
  // the rail icon into the flyout so it doesn't flicker.
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openFlyout = (key: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => setHoveredGroupKey(key), 120);
  };
  const keepFlyoutOpen = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };
  const scheduleCloseFlyout = () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setHoveredGroupKey(null), 200);
  };
  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
      if (openTimer.current) clearTimeout(openTimer.current);
    },
    [],
  );

  // Which first-level entry owns the current route, narrowed to entries that
  // can actually be unfolded: a single-page item (Dashboard, Network) owns a
  // one-entry flyout on the rail but has no second level in the column, so
  // unfolding it would only pollute the persisted list.
  //
  // A plain string, so the effect below keys off a stable value rather than the
  // `nav` array, whose identity changes on every render of the shell.
  const activeSpecKey =
    panelSpecs.find(spec => isSpecActive(spec, pathname))?.key ?? null;
  const activeGroupKey =
    activeSpecKey &&
    nav.some(e => e.type === 'group' && e.key === activeSpecKey)
      ? activeSpecKey
      : null;

  // Landing on a page unfolds the group that owns it, so the column can always
  // say where you are without you having to open anything. `openGroup` is a
  // no-op when the group is already unfolded, so this cannot fight a manual
  // fold on the page you are already on.
  useEffect(() => {
    if (activeGroupKey) openGroup(activeGroupKey);
  }, [activeGroupKey, openGroup]);

  // Collapse the hover flyout as soon as the route changes — otherwise it
  // lingers after you've navigated.
  useEffect(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setHoveredGroupKey(null);
  }, [pathname]);

  // Touch devices get the column unconditionally: a flyout that only opens on
  // hover is unreachable there.
  const asColumn = expanded || isMobile;
  const flyoutSpec = asColumn
    ? undefined
    : panelSpecs.find(spec => spec.key === hoveredGroupKey);

  /** Expand the column onto `key`, folding the sections you didn't pick. */
  const revealGroup = (key: string) => {
    openOnlyGroup(key);
    setPanelOpen(true);
  };

  // Section headings have nowhere to go on an icon-only rail. Dividers mark the
  // two outer group boundaries: after the first section and before the last.
  const sectionIdxs: number[] = [];
  nav.forEach((e, i) => {
    if (e.type === 'section') sectionIdxs.push(i);
  });
  const firstSectionIndex = sectionIdxs[0];
  const lastSectionIndex = sectionIdxs[sectionIdxs.length - 1];

  /** Single-page entries are destinations in their own right. */
  const itemActive = (entry: NavEntry & { type: 'item' }) =>
    entry.href
      ? isPathActive(pathname, entry.href) ||
        (entry.key === 'dashboard' && pathname === '/')
      : false;

  // ---------- The expanded column ----------

  function renderColumn() {
    return (
      <nav className='flex h-full w-full min-w-0 flex-col'>
        {/* Logo band matches the 52px header row height so the mark lines up
            with the app header beside it. */}
        <div className='flex h-13 shrink-0 items-center px-3.5'>
          <RetracedMark className='size-5 min-[1600px]:size-6 text-brand-accent' />
        </div>

        <div
          className={cn(
            'min-h-0 flex-1 overflow-y-auto overflow-x-hidden pt-2 pb-2',
            columnPad,
          )}
        >
          <div className={columnStack}>
            {nav.map((entry, index) => {
              if (entry.type === 'section') {
                return (
                  <ColumnSection
                    key={`section-${index}-${entry.label}`}
                    label={entry.label}
                  />
                );
              }

              if (entry.type === 'item') {
                return (
                  <ColumnItem
                    key={entry.key}
                    item={entry}
                    active={itemActive(entry)}
                    onNavigate={
                      isMobile ? () => setMobileOpen(false) : undefined
                    }
                  />
                );
              }

              const visible = entry.children.filter(
                c => !c.visible || c.visible(),
              );
              if (visible.length === 0) return null;
              return (
                <ColumnGroup
                  key={entry.key}
                  group={entry}
                  items={visible}
                  open={openGroupKeys.includes(entry.key)}
                  activeKey={activeChildKey(visible, pathname)}
                  onToggle={() => toggleGroupOpen(entry.key)}
                  onNavigate={isMobile ? () => setMobileOpen(false) : undefined}
                />
              );
            })}
          </div>
        </div>

        {/* Collapsing is meaningless on touch — the rail's flyout needs a
            hover the device cannot produce — so the control is desktop-only. */}
        {!isMobile && (
          <div className={cn('shrink-0 pt-2 pb-2', columnPad)}>
            <hr className='-mx-2 mb-2 border-0 border-t border-sidebar-foreground/20 min-[1600px]:-mx-2.5' />
            <button
              type='button'
              // Anchor for the onboarding tour's expand/collapse step.
              data-tour='sidebar-toggle'
              onClick={() => setPanelOpen(false)}
              className={cn(rowBase, rowInactive)}
            >
              <span className={rowIcon}>
                <FontAwesomeIcon icon={faChevronsLeft} className='size-4' />
              </span>
              <span className='min-w-0 flex-1 truncate'>
                {getTranslation('collapseSidebar')}
              </span>
            </button>
          </div>
        )}
      </nav>
    );
  }

  // ---------- The collapsed rail ----------

  function renderRail() {
    return (
      <nav
        // Denser below 1600px, roomier from 1600px up. Sized to the icon pill
        // plus padding since there are no labels to fit. The rail itself does
        // NOT scroll — only the icon column does — so the chevron below it
        // stays pinned to the bottom instead of scrolling out of reach.
        className='shrink-0 flex flex-col pb-2 overflow-x-hidden w-14 px-2 min-[1600px]:w-16 min-[1600px]:px-2.5'
      >
        <div className='flex h-13 shrink-0 items-center justify-center'>
          <RetracedMark className='size-5 min-[1600px]:size-6 text-brand-accent' />
        </div>
        <div className='min-h-0 flex-1 overflow-y-auto overflow-x-hidden flex flex-col gap-2 min-[1600px]:gap-3.5 pt-2'>
          {nav.map((entry, index) => {
            if (entry.type === 'section') {
              return index === firstSectionIndex ||
                index === lastSectionIndex ? (
                <hr
                  key={`rail-divider-${index}`}
                  className='border-0 border-t border-sidebar-foreground/20 mx-2 my-1'
                />
              ) : null;
            }

            if (entry.type === 'item') {
              return (
                <StripItem
                  key={entry.key}
                  item={entry}
                  active={itemActive(entry)}
                  // In-app destinations re-expand the column on arrival;
                  // external links open a tab and change nothing here.
                  onNavigate={entry.href ? () => setPanelOpen(true) : undefined}
                />
              );
            }

            const visible = entry.children.filter(
              c => !c.visible || c.visible(),
            );
            if (visible.length === 0) return null;
            const anyChildActive = visible.some(c =>
              isPathActive(pathname, c.matchPrefix ?? c.href),
            );
            return (
              <StripGroup
                key={entry.key}
                group={entry}
                selected={hoveredGroupKey === entry.key}
                anyChildActive={anyChildActive}
                // Named by a tooltip like every other rail icon. The only
                // moment it is suppressed is while this group's own flyout is
                // showing — the flyout already names it, and the tooltip would
                // render on top of it.
                showTooltip={hoveredGroupKey !== entry.key}
                {...(visible[0]?.href
                  ? { firstChildHref: visible[0].href }
                  : {})}
                onMouseEnter={() => openFlyout(entry.key)}
                onMouseLeave={scheduleCloseFlyout}
                onClick={() => revealGroup(entry.key)}
              />
            );
          })}

          {/* The empty rail below the icons expands the sidebar too. It is a
              real <button> filling the leftover space rather than a click
              handler on the rail, so it is focusable and needs no a11y escape
              hatch. */}
          <button
            type='button'
            aria-label={getTranslation('expandSidebar')}
            onClick={() => setPanelOpen(true)}
            className='flex-1 min-h-8 cursor-pointer rounded-lg'
          />
        </div>

        {/* Expand — pinned to the bottom of the rail, below the icon column
            (which takes the leftover height via `flex-1`). The divider uses the
            same rule as the rail's section dividers so it reads as one system. */}
        <div className='shrink-0 pt-2'>
          {/* Negative margins cancel the rail's own `px-2` / `px-2.5` so the
              rule spans the full sidebar width, edge to edge. */}
          <hr className='border-0 border-t border-sidebar-foreground/20 -mx-2 min-[1600px]:-mx-2.5 mb-2' />
          <div className='flex justify-center'>
            <IconButton
              variant='ghost'
              size='icon'
              tooltipSide='right'
              // Anchor for the onboarding tour's expand/collapse step.
              data-tour='sidebar-toggle'
              tooltip={getTranslation('expandSidebar')}
              className={cn(
                'size-8 hover:text-sidebar-foreground! hover:bg-brand-accent/15',
                restingFg,
              )}
              onClick={() => setPanelOpen(true)}
            >
              <FontAwesomeIcon icon={faChevronsRight} className='size-4' />
            </IconButton>
          </div>
        </div>
      </nav>
    );
  }

  // ---------- The hover flyout, collapsed only ----------

  function renderFlyout(spec: PanelSpec) {
    const panelActiveKey = activeChildKey(spec.children, pathname);
    return (
      <nav
        className='absolute left-14 min-[1600px]:left-16 top-0 bottom-0 w-50 z-50 flex flex-col overflow-hidden border-r border-sidebar-foreground/10 bg-sidebar text-sidebar-foreground animate-in fade-in slide-in-from-left-3 duration-300 ease-out'
        onMouseEnter={keepFlyoutOpen}
        onMouseLeave={scheduleCloseFlyout}
      >
        <div className='flex flex-1 flex-col overflow-y-auto px-2 pb-3'>
          {/* Header sits in a band the height of the logo/header row (h-13) so
              the sub-items below line up with the rail's first icon (Home). */}
          <div className='flex h-13 shrink-0 items-center justify-between px-2.5'>
            <p className='select-none text-sm font-bold tracking-tight text-sidebar-foreground'>
              {spec.label}
            </p>
            <IconButton
              variant='ghost'
              size='icon'
              tooltip={getTranslation('expandSidebar')}
              className={cn(
                'w-5 h-5 hover:text-sidebar-foreground! hover:bg-brand-accent/15',
                restingFg,
              )}
              onClick={() => revealGroup(spec.key)}
            >
              <FontAwesomeIcon icon={faChevronsRight} className='text-xs' />
            </IconButton>
          </div>
          <div className='flex flex-col gap-1.5 pt-2'>
            {spec.children.map(child => {
              const active = child.key === panelActiveKey;
              return (
                <Link
                  key={child.key}
                  to={child.href as never}
                  // Picking a page here re-expands the column on that section,
                  // so the collapsed rail is a shortcut, not a dead end.
                  onClick={() => revealGroup(spec.key)}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    childBase,
                    'min-[1600px]:h-9',
                    active ? childSelected : childInactive,
                  )}
                >
                  <span className='truncate'>{child.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>
    );
  }

  const sidebarContent = (
    <aside
      id={sidebarId}
      // Anchor for the onboarding tour's first step — see `useOnboardingTour`.
      data-tour='sidebar'
      className={cn(
        'relative flex h-full bg-sidebar text-sidebar-foreground transition-[width] duration-300 ease-out',
        asColumn
          ? isMobile
            ? 'w-full'
            : expandedColumnWidthCls
          : 'w-14 min-[1600px]:w-16',
      )}
    >
      {asColumn ? (
        renderColumn()
      ) : (
        <>
          {renderRail()}
          {flyoutSpec && renderFlyout(flyoutSpec)}
        </>
      )}
    </aside>
  );

  if (isMobile) {
    return (
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side='left' showCloseButton={false} className='p-0 !w-72'>
          {sidebarContent}
        </SheetContent>
      </Sheet>
    );
  }

  return <div className='row-span-2 row-start-1'>{sidebarContent}</div>;
}
